import { OperatorPerformance } from '@hakimo-ui/hakimo/types';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { FreshnessLabel } from './FreshnessLabel';
import { METRICS } from './metrics';
import { PerformanceTrendChart } from './PerformanceTrendChart';
import { fillBuckets, formatRate, Freshness, getWindowHours } from './utils';

interface Props {
  data: OperatorPerformance;
  freshness: Freshness;
}

export function OperatorPerformanceDetails(props: Props) {
  const { data, freshness } = props;
  const buckets = useMemo(() => fillBuckets(data), [data]);
  const tileClassName =
    'bg-onlight-bg-2 dark:bg-ondark-bg-2 flex flex-col gap-1 rounded-lg px-3.5 py-3';

  return (
    <div className="flex flex-col gap-3.5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <h2 className="text-onlight-text-1 dark:text-ondark-text-1 text-sm font-semibold">
            Your performance
          </h2>
          <span className="text-onlight-text-2 dark:text-ondark-text-2 text-xs">
            Last {getWindowHours(data)} hours ·{' '}
            {dayjs(data.windowStart).format('HH:mm')} –{' '}
            {dayjs(data.windowEnd).format('HH:mm')}
          </span>
        </div>
        <FreshnessLabel freshness={freshness} />
      </div>
      <div
        className={clsx(
          'flex flex-col gap-3.5',
          freshness.delayed && 'opacity-60'
        )}
      >
        <div className="grid grid-cols-2 gap-2.5">
          {METRICS.map((metric) => (
            <div key={metric.key} className={tileClassName}>
              <span className="text-onlight-text-2 dark:text-ondark-text-2 flex items-center gap-1.5 text-xs">
                <span className={metric.swatchClass} />
                {metric.label}
              </span>
              <span className="text-onlight-text-1 dark:text-ondark-text-1 text-2xl font-semibold">
                {data.totals[metric.key]}
              </span>
            </div>
          ))}
          <div className={tileClassName}>
            <span className="text-onlight-text-2 dark:text-ondark-text-2 text-xs">
              Median resolution rate
            </span>
            <span className="text-onlight-text-1 dark:text-ondark-text-1 text-2xl font-semibold">
              {formatRate(data.totals.medianResolutionRate)}
              <span className="text-onlight-text-2 dark:text-ondark-text-2 text-sm font-medium">
                {' '}
                /{data.resolutionMinutes} min
              </span>
            </span>
          </div>
        </div>
        <PerformanceTrendChart
          buckets={buckets}
          resolutionMinutes={data.resolutionMinutes}
        />
      </div>
    </div>
  );
}

export default OperatorPerformanceDetails;
